/**
 * @fileoverview This file provides functionality to automatically generate and update `@fileoverview` comments for TypeScript and JavaScript files using AI. It supports both inserting missing comments and updating existing ones. The file processes files in parallel for efficiency.
 */

import * as fs from 'fs'
import * as path from 'path'
import { Model } from '@iterator/shared-node/ai/llm_types'
import { FileData, getTokenCount, walkFolderAsync } from './folder'
import { print_ai } from '@iterator/shared-node/util/logging'
import { SingleTonUsageTracker, trackUsage } from '@iterator/shared-node/util/usage_tracker'
import { error } from 'console'

export const FILE_OVERVIEW_REGEX = /\/\*\*\s*\n\s*\*\s*@fileoverview\s+([\s\S]*?)\s*\*\//

interface FileToProcess {
  path: string
  relativePath: string
}

function isValidFile(filePath: string): boolean {
  return filePath.endsWith('.ts') || filePath.endsWith('.js') || filePath.endsWith('.tsx')
}

function getFileContents(filePath: string): FileData {
  return {
    path: filePath,
    contents: fs.readFileSync(filePath, 'utf8'),
    tokenCount: 0,
  }
}

function hasFileOverview(contents: string): boolean {
  return FILE_OVERVIEW_REGEX.test(contents)
}

async function generateFileOverview(file: FileData, model: Model): Promise<string> {
  const prompt = `Generate a concise @fileoverview comment for this TypeScript/JavaScript file. Focus on the main purpose and functionality of the file. It should include the names of key classes, types and functions. The comment should be 1-3 sentences.

File: ${file.path}
Contents:
${file.contents}

Return ONLY the text that should go in the @fileoverview, without the comment syntax.`

  try {
    const overview = await model
      .getApi()
      .streamChatCompletions(undefined, [], [{ role: 'user', content: prompt }], () => {})
    trackUsage('generateFileOverview - ' + file.path, overview.usage, model)
    return overview.message.content.trim()
  } catch (e) {
    error(`Error generating file overview for ${file.path}: ${e}`)
    return ''
  }
}

function updateFileWithOverview(filePath: string, overview: string) {
  const contents = fs.readFileSync(filePath, 'utf8')
  const fileOverviewComment = `/**\n * @fileoverview ${overview}\n */\n\n`

  const hasShebang = contents.startsWith('#!')
  const shebangLine = hasShebang ? contents.split('\n')[0] + '\n' : ''
  const contentWithoutShebang = hasShebang ? contents.split('\n').slice(1).join('\n') : contents

  if (contentWithoutShebang.match(FILE_OVERVIEW_REGEX)) {
    const updatedContents = contentWithoutShebang.replace(
      FILE_OVERVIEW_REGEX,
      fileOverviewComment.trim(),
    )
    fs.writeFileSync(filePath, shebangLine + updatedContents)
  } else {
    fs.writeFileSync(filePath, shebangLine + fileOverviewComment + contentWithoutShebang)
  }
}

async function processFile(
  file: FileToProcess,
  model: Model,
  mode: 'insert' | 'update',
): Promise<boolean> {
  const fileData = getFileContents(file.path)
  const hasOverview = hasFileOverview(fileData.contents)

  if (!hasOverview || mode === 'update') {
    print_ai(`${mode === 'update' ? 'Updating' : 'Adding'} @fileoverview for ${file.relativePath}`)
    const overview = await generateFileOverview(fileData, model)
    if (overview) {
      updateFileWithOverview(file.path, overview)
      return true
    }
  }
  return false
}

async function getFilesFromDirectory(directory: string): Promise<FileToProcess[]> {
  const files: FileToProcess[] = []
  await walkFolderAsync(directory, async (filePath, type) => {
    if (type === 'file' && isValidFile(filePath)) {
      files.push({
        path: filePath,
        relativePath: path.relative(directory, filePath),
      })
    }
  })
  return files
}

function validateAndNormalizeFiles(directory: string, files: string[]): FileToProcess[] {
  return files
    .filter((f) => isValidFile(f))
    .map((f) => {
      const fullPath = path.isAbsolute(f) ? f : path.join(directory, f)
      return {
        path: fullPath,
        relativePath: path.relative(directory, fullPath),
      }
    })
}

async function processFilesInParallel(
  files: FileToProcess[],
  model: Model,
  mode: 'insert' | 'update',
  maxConcurrent: number = 100,
): Promise<{ processed: number; updated: number }> {
  let processed = 0
  let updated = 0
  let currentIndex = 0

  const runningPromises = new Set<Promise<void>>()

  while (currentIndex < files.length || runningPromises.size > 0) {
    // Start new tasks if we're under the limit and have more files
    while (runningPromises.size < maxConcurrent && currentIndex < files.length) {
      const file = files[currentIndex++]!
      const promise = processFile(file, model, mode).then((wasUpdated) => {
        processed++
        if (wasUpdated) updated++
        runningPromises.delete(promise)
      })
      runningPromises.add(promise)
    }

    // Wait for at least one promise to complete before continuing
    if (runningPromises.size > 0) {
      await Promise.race(Array.from(runningPromises))
    }
  }

  return { processed, updated }
}

export async function documentFiles(
  directory: string,
  model: Model,
  specificFiles?: string[],
  mode: 'insert' | 'update' = 'insert',
  dryRun: boolean = false,
) {
  const files = specificFiles
    ? validateAndNormalizeFiles(directory, specificFiles)
    : await getFilesFromDirectory(directory)

  print_ai(`Processing ${files.length} files in ${directory}`)

  if (dryRun) {
    let processedCount = 0
    let totalTokens = 0
    process.stdout.write('\n')

    for (const file of files) {
      const fileData = getFileContents(file.path)
      totalTokens += getTokenCount(fileData.contents)
      processedCount++
      process.stdout.write(`\rProcessed ${processedCount}/${files.length}`)
    }

    process.stdout.write('\n')
    print_ai(`\nEstimated cost to document ${files.length} files:
    Prompt tokens: ${totalTokens}
    Completion tokens: ${files.length * 100}
    Total cost: $${totalTokens * model.promptTokenCost + files.length * 100 * model.completionTokenCost}`)
    return
  }

  const { processed: filesProcessed, updated: filesUpdated } = await processFilesInParallel(
    files,
    model,
    mode,
  )
  print_ai(`Processed ${filesProcessed} files, updated ${filesUpdated} files`)
  SingleTonUsageTracker.log('documentFiles')
}
